import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, Clock, MapPin, Package, Truck, FileText, Receipt, Download, Mail, X, Hash, Loader2 } from 'lucide-react';
import { downloadVatInvoiceUrl, emailReceipt } from '../services/api';
import { toast } from 'sonner';
import LiftingHelpSummary from './LiftingHelpSummary';
import { PremiumBadge } from './PremiumBadge';

/**
 * Detail + Receipt dialog for a customer's past delivery.
 *
 * Two-panel UX:
 *   - "details": clean Uber-style timeline, addresses, item, driver, vehicle.
 *   - "receipt": VAT-invoice / receipt summary with Download PDF + Email Receipt actions.
 *
 * Props:
 *   delivery:     full delivery doc (or compact — we render whatever is present)
 *   driverProfile?: optional `{vehicle_type, ...}` enrichment passed by parent
 *   open:         boolean
 *   onOpenChange: (open) => void
 */
export const DeliveryDetailDialog = ({ delivery, driverProfile, open, onOpenChange }) => {
  const [view, setView] = useState('details'); // 'details' | 'receipt'
  const [emailing, setEmailing] = useState(false);
  if (!delivery) return null;

  const handleClose = (next) => {
    onOpenChange?.(next);
    if (!next) setView('details');
  };

  const handleEmailReceipt = async () => {
    setEmailing(true);
    try {
      await emailReceipt(delivery.id);
      toast.success('Receipt emailed to your inbox', { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setEmailing(false);
    }
  };

  // Direct bookings have nothing to capture — JustDeliver's charges are paid
  // online at booking, so the receipt is ready immediately.
  const isDirect = delivery.payment_model === 'pay_driver_directly' || delivery.payment_method === 'direct';
  const captured = isDirect
    ? delivery.payment_status === 'fees_paid'
    : delivery.capture_status === 'captured';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl p-0" data-testid="delivery-detail-dialog">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              {view === 'details' ? <Package className="w-5 h-5 text-[#2E7D32]" /> : <Receipt className="w-5 h-5 text-[#2E7D32]" />}
              {view === 'details' ? 'Delivery details' : 'Receipt'}
            </DialogTitle>
            {view === 'receipt' && (
              <button
                onClick={() => setView('details')}
                className="text-xs text-gray-500 hover:text-gray-800 underline"
                data-testid="back-to-details-btn"
              >
                ← Back to details
              </button>
            )}
          </div>
          {delivery.delivery_ref && (
            <p
              className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs font-bold text-[#1B5E20] bg-emerald-50 border border-emerald-100 px-2 py-1 rounded w-fit"
              data-testid="detail-delivery-ref"
            >
              <Receipt className="w-3 h-3" />
              {delivery.delivery_ref}
            </p>
          )}
        </DialogHeader>

        {view === 'details' ? (
          <DetailsView delivery={delivery} driverProfile={driverProfile} onShowReceipt={() => setView('receipt')} captured={captured} />
        ) : (
          <ReceiptView delivery={delivery} onEmailReceipt={handleEmailReceipt} emailing={emailing} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Details View ──────────────────────────────────────────────────────────

const DetailsView = ({ delivery, driverProfile, onShowReceipt, captured }) => {
  const total = (delivery.assigned_price || 0) + (delivery.platform_fee || 0);

  return (
    <div className="px-5 py-4 space-y-5">
      {/* Status banner */}
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm" data-testid="detail-status">
        <CheckCircle className="w-4 h-4 text-emerald-600" />
        <span className="font-semibold text-emerald-800">Delivered</span>
        <span className="text-emerald-700/80 ml-1">{shortDate(delivery.delivered_at) || '—'}</span>
      </div>

      {/* Addresses */}
      <div className="space-y-3">
        <AddressBlock label="Pickup" address={delivery.pickup_location} county={delivery.pickup_county} eircode={delivery.pickup_eircode} color="#2E7D32" testId="detail-pickup" />
        <AddressBlock label="Drop-off" address={delivery.dropoff_location} county={delivery.dropoff_county} eircode={delivery.dropoff_eircode} color="#111827" testId="detail-dropoff" />
        {Array.isArray(delivery.additional_drops) && delivery.additional_drops.length > 0 && (
          <div className="ml-6 pl-3 border-l-2 border-blue-200 text-sm space-y-2" data-testid="detail-additional-drops">
            <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Additional drops</p>
            {/* Prefer the rich `drops` array if present (per-drop receiver + description),
                fall back to the legacy address-only list otherwise. */}
            {Array.isArray(delivery.drops) && delivery.drops.length > 0 ? (
              delivery.drops.map((d, i) => (
                <div key={i} className="text-gray-700" data-testid={`detail-drop-${i}`}>
                  <p className="font-semibold text-gray-800">
                    {i + 2}. {d.address}
                    {d.eircode && (
                      <span className="ml-2 inline-block text-[11px] font-bold tracking-wider px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono align-middle">
                        {d.eircode}
                      </span>
                    )}
                  </p>
                  {(d.receiver_name || d.receiver_phone) && (
                    <p className="text-xs text-gray-500 ml-3">
                      Receiver: {d.receiver_name || '—'}{d.receiver_phone ? ` · ${d.receiver_phone}` : ''}
                    </p>
                  )}
                  {d.item_description && (
                    <p className="text-xs text-gray-500 italic ml-3">"{d.item_description}"</p>
                  )}
                  {(d.dimensions_length || d.dimensions_width || d.dimensions_height || d.weight_kg) && (
                    <p className="text-xs text-gray-500 ml-3 font-mono tabular-nums">
                      {[
                        (d.dimensions_length || d.dimensions_width || d.dimensions_height) &&
                          `${d.dimensions_length ?? '–'}×${d.dimensions_width ?? '–'}×${d.dimensions_height ?? '–'}cm`,
                        d.weight_kg && `${d.weight_kg}kg`,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {Array.isArray(d.photos) && d.photos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 ml-3 mt-1">
                      {d.photos.map((src, pIdx) => (
                        <a
                          key={pIdx}
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50 hover:ring-2 hover:ring-blue-300"
                        >
                          <img src={src} alt={`Drop ${i + 2} photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              delivery.additional_drops.map((d, i) => (
                <p key={i} className="text-gray-700">{i + 2}. {d}</p>
              ))
            )}
          </div>
        )}
      </div>

      <Divider />

      {/* Item + Driver block */}
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Field label="Item" value={delivery.item_description} icon={FileText} testId="detail-item" />
        <Field label="Item type" value={delivery.item_type} icon={Package} capitalize testId="detail-item-type" />
        <div className="space-y-0.5" data-testid="detail-driver">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-1">
            <Truck className="w-3 h-3" /> Driver
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm text-gray-800">{delivery.assigned_driver_name || '—'}</p>
            {delivery.assigned_driver_is_premium && <PremiumBadge size="sm" dataTestId="detail-driver-premium-badge" />}
          </div>
        </div>
        <Field label="Vehicle" value={driverProfile?.vehicle_type || '—'} icon={Truck} capitalize testId="detail-vehicle" />
        {delivery.weight_kg && <Field label="Weight" value={`${delivery.weight_kg} kg`} testId="detail-weight" />}
        {delivery.distance_km != null && <Field label="Distance" value={`${delivery.distance_km} km`} testId="detail-distance" />}
      </div>

      <Divider />

      {/* Lifting & loading recap */}
      {(delivery.collection_help || delivery.delivery_help || delivery.driver_assistance) && (
        <>
          <LiftingHelpSummary
            collectionHelp={delivery.collection_help}
            deliveryHelp={delivery.delivery_help}
            driverAssistance={delivery.driver_assistance}
            dataTestId="detail-lifting-help"
          />
          <Divider />
        </>
      )}

      {/* Timeline */}
      <Timeline delivery={delivery} />

      <Divider />

      {/* Totals */}
      <div className="bg-gray-50 rounded-md p-3 text-sm space-y-1.5" data-testid="detail-totals">
        <Row label="Driver bid" value={fmtEuro(delivery.assigned_price)} />
        <Row label="Booking Deposit" value={fmtEuro(delivery.booking_deposit_amount ?? delivery.platform_fee)} muted />
        <Row label="JustDeliver Driver Fee" value={fmtEuro(delivery.driver_deduction_amount ?? 0)} muted />
        <div className="border-t border-gray-200 pt-2 mt-1 space-y-1.5">
          <Row label="Paid to JustDeliver online" value={fmtEuro(delivery.online_payment_amount ?? delivery.platform_fee)} bold />
          <Row label="Paid directly to driver" value={fmtEuro(delivery.driver_payout_amount ?? delivery.direct_balance_due ?? delivery.assigned_price)} bold />
          <Row label="Total" value={fmtEuro(total)} bold large />
        </div>
      </div>

      {/* CTA */}
      {captured ? (
        <Button onClick={onShowReceipt} className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2" data-testid="view-receipt-btn">
          <Receipt className="w-4 h-4" /> View Receipt
        </Button>
      ) : (
        <p className="text-xs text-gray-500 text-center" data-testid="receipt-not-ready">
          Receipt becomes available once your payment to JustDeliver has gone through.
        </p>
      )}
    </div>
  );
};

// ── Receipt View ──────────────────────────────────────────────────────────

const ReceiptView = ({ delivery, onEmailReceipt, emailing }) => {
  const total = (delivery.assigned_price || 0) + (delivery.platform_fee || 0);

  return (
    <div className="px-5 py-4 space-y-4" data-testid="receipt-view">
      {/* Receipt header */}
      <div className="flex items-start justify-between border-b border-gray-100 pb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Receipt #</p>
          <p className="font-mono text-sm text-gray-800 flex items-center gap-1" data-testid="receipt-job-id">
            <Hash className="w-3 h-3" />{delivery.delivery_code || delivery.id?.slice(-8) || '—'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</p>
          <p className="text-sm text-gray-800">{shortDate(delivery.captured_at || delivery.delivered_at)}</p>
        </div>
      </div>

      {/* Locations */}
      <div className="space-y-2 text-sm">
        <ReceiptLine label="From" value={delivery.pickup_location} />
        <ReceiptLine label="To" value={delivery.dropoff_location} />
        <ReceiptLine label="Driver" value={delivery.assigned_driver_name || '—'} />
      </div>

      <Divider />

      {/* Breakdown */}
      <div className="space-y-1.5 text-sm" data-testid="receipt-breakdown">
        <Row label="Driver bid" value={fmtEuro(delivery.assigned_price)} />
        <Row label="Booking Deposit" value={fmtEuro(delivery.booking_deposit_amount ?? delivery.platform_fee)} muted />
        <Row label="JustDeliver Driver Fee" value={fmtEuro(delivery.driver_deduction_amount ?? 0)} muted />
        <div className="border-t border-gray-200 pt-2 mt-1 space-y-1.5">
          <Row label="Paid to JustDeliver online" value={fmtEuro(delivery.online_payment_amount ?? delivery.platform_fee)} bold />
          <Row label="Paid directly to driver" value={fmtEuro(delivery.driver_payout_amount ?? delivery.direct_balance_due ?? delivery.assigned_price)} bold />
          <Row label="Total for this delivery" value={fmtEuro(total)} bold large />
        </div>
      </div>

      {/* Payment method */}
      <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-600 flex items-center justify-between" data-testid="receipt-payment-method">
        <span>JustDeliver charges paid by card via Stripe</span>
        {delivery.stripe_payment_intent_id && (
          <span className="font-mono text-[10px] text-gray-400">{delivery.stripe_payment_intent_id.slice(-12)}</span>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <a
          href={downloadVatInvoiceUrl(delivery.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md bg-[#2E7D32] text-white text-sm font-semibold hover:bg-[#1B5E20]"
          data-testid="receipt-download-pdf-btn"
        >
          <Download className="w-4 h-4" /> Download PDF
        </a>
        <button
          onClick={onEmailReceipt}
          disabled={emailing}
          className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md border border-[#2E7D32] text-[#2E7D32] text-sm font-semibold hover:bg-emerald-50 disabled:opacity-60"
          data-testid="receipt-email-btn"
        >
          {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {emailing ? 'Sending…' : 'Email receipt'}
        </button>
      </div>

      <p className="text-[11px] text-gray-400 text-center pt-1">
        JustDeliver Ltd · Delivery service provided by the named driver, who is paid directly by the customer. The Booking Deposit and JustDeliver Driver Fee are collected online by JustDeliver Ltd for secure payments, fraud prevention, customer support and cancellation refund protection.
      </p>
    </div>
  );
};

// ── small helpers ─────────────────────────────────────────────────────────

const Divider = () => <div className="border-t border-gray-100" />;

const AddressBlock = ({ label, address, county, eircode, color, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="w-6 flex-shrink-0 flex items-start justify-center pt-0.5">
      <MapPin className="w-5 h-5" style={{ color }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">{label}</p>
      <p className="text-sm text-gray-900 font-medium break-words">{address || '—'}</p>
      {(county || eircode) && (
        <p className="text-xs text-gray-500">{[county, eircode].filter(Boolean).join(' · ')}</p>
      )}
    </div>
  </div>
);

const Field = ({ label, value, icon: Icon, capitalize = false, testId }) => (
  <div className="space-y-0.5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </p>
    <p className={`text-sm text-gray-800 ${capitalize ? 'capitalize' : ''}`}>{value || '—'}</p>
  </div>
);

const Row = ({ label, value, bold = false, large = false, muted = false }) => (
  <div className="flex items-center justify-between">
    <span className={`${bold ? 'font-semibold text-gray-900' : muted ? 'text-gray-500 text-xs' : 'text-gray-600'} ${large ? 'text-base' : ''}`}>{label}</span>
    <span className={`${bold ? 'font-bold text-[#1B5E20]' : muted ? 'text-gray-500 text-xs' : 'text-gray-800'} ${large ? 'text-base' : ''}`}>{value}</span>
  </div>
);

const ReceiptLine = ({ label, value }) => (
  <div className="flex items-start gap-3">
    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold w-12 flex-shrink-0 pt-0.5">{label}</span>
    <span className="text-gray-800 break-words">{value || '—'}</span>
  </div>
);

const TIMELINE_STEPS = [
  { key: 'created_at',   label: 'Job posted' },
  { key: 'accepted_at',  label: 'Accepted' },
  { key: 'picked_up_at', label: 'Collected' },
  { key: 'delivered_at', label: 'Delivered' },
];

const Timeline = ({ delivery }) => (
  <div className="space-y-2" data-testid="detail-timeline">
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Timeline</p>
    <ol className="space-y-2">
      {TIMELINE_STEPS.map(({ key, label }, idx) => {
        const ts = delivery[key];
        const reached = !!ts;
        const isLast = idx === TIMELINE_STEPS.length - 1;
        return (
          <li key={key} className="relative pl-7" data-testid={`timeline-${key}`}>
            <span className={`absolute left-1.5 top-1.5 w-2.5 h-2.5 rounded-full ring-2 ${reached ? 'bg-[#2E7D32] ring-emerald-100' : 'bg-gray-300 ring-gray-100'}`} />
            {!isLast && <span className={`absolute left-[10px] top-4 w-px h-full ${reached ? 'bg-emerald-200' : 'bg-gray-200'}`} />}
            <div className="flex items-center justify-between text-sm gap-2">
              <span className={reached ? 'font-medium text-gray-900' : 'text-gray-400'}>{label}</span>
              <span className="text-gray-500 text-xs whitespace-nowrap">{fullDateTime(ts) || '—'}</span>
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);

const fmtEuro = (v) => (v == null ? '€0.00' : `€${Number(v).toFixed(2)}`);

const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fullDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default DeliveryDetailDialog;
